<template>
  <div @click="onClick" class="editor__wrapper">
    <mce-editor
      v-if="value"
      v-show="!cantUseTinyMCE"
      :tinymceScriptSrc="'https://d25510ev64x81w.cloudfront.net/js/tinymce/5.10.5/tinymce.min.js'"
      :id="inputId"
      :ref="inputId"
      class="mce-editor"
      :class="{
        'has-border': hasBorder,
        'opacity-0': hasClassOpacity0,
        'light-text-colour': isTextColourLight,
        'clickable': clickable
      }"
      :disabled="disabled"
      :init="initOptions"
      inline
      :initial-value="initialValue"
      :value="dataValue"
      @onBlur="onBlur"
      @onFocus="onFocus"
    />
    <p v-show="cantUseTinyMCE">
      {{ textValue }}
    </p>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import mceEditor from '@tinymce/tinymce-vue'
import debounce from 'debounce'
import configs from '@/config'
import util from '@/util'
import gaCategories from '@/constant/gaCategories'
import ClickableMixin from '@/mixins/text-editor/ClickableMixin'
import EventBus from '@/eventBus'

export default {
  components: {
    mceEditor,
  },
  mixins: [ClickableMixin],
  props: {
    maxChars: {
      type: Number,
      default: 300,
    },
    type: {
      type: String,
      default: 'slide-title',
    },
    id: {
      type: [String, Number],
      default: null,
    },
    undo: {
      type: Boolean,
      default: true,
    },
    redo: {
      type: Boolean,
      default: true,
    },
    bold: {
      type: Boolean,
      default: false,
    },
    italic: {
      type: Boolean,
      default: true,
    },
    strikethrough: {
      type: Boolean,
      default: true,
    },
    align: {
      type: Boolean,
      default: true,
    },
    color: {
      type: Boolean,
      default: true,
    },
    underline: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      default: '',
    },
    initialValue: {
      type: String,
      default: '',
    },
    hasBorder: {
      type: Boolean,
      default: false,
    },
    isTextColourLight: {
      type: Boolean,
      default: false,
    },
    isPresentingDefaultContent: {
      type: Boolean,
      default: false,
    },
  },
  data: () => {
    return {
      mceEditorKey: configs.mceEditorKey,
      isInputOnFocus: false,
      mceEditorInstance: null,
      cantUseTinyMCE: false,
    }
  },
  computed: {
    ...mapGetters(['isPresenting', 'isBackstage']),
    ...mapState({
      currentUser: (state) => state.currentUser,
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      showHyperLink: (state) => state.presentation.showHyperLink,
    }),
    inputId() {
      return `mce-editor-${this.type}-${this.id}`
    },
    hasClassOpacity0() {
      return this.disabled && this.isPresentingDefaultContent
    },
    textValue() {
      return util.extractContent(this.value, true)
    },
  },
  watch: {
    isPresenting: function watchPresenting() {
      if (!this.isPresenting) this._setInputBlur()
    },

  },
  created() {
    const self = this
    this.cantUseTinyMCE = true

    this.initOptions = {
      menubar: false,
      plugins: `
        autolink link paste wordcount
      `,
      toolbar: `
        fontselect | 
        ${self.bold ? 'bold ' : ''}
        ${self.italic ? 'italic ' : ''}
        ${self.underline ? 'underline ' : ''}
        ${self.strikethrough ? 'strikethrough ' : ''}
        ${self.color ? 'forecolor backcolor ' : ''} |
        alignleft aligncenter alignright | removeformat
      `,
      font_formats: 'Nunito=Nunito; Roboto=Roboto; Roboto Slab=Roboto Slab; Open Sans=Open Sans; Oswald=Oswald; Montserrat=Montserrat; Source Code Pro=Source Code Pro; Merriweather=Merriweather; EB Garamond=EB Garamond; Cairo=Cairo; Kanit=Kanit',
      default_link_target: '_blank',
      paste_data_images: false,
      paste_as_text: true,
      remove_linebreaks: true,
      remove_trailing_brs: true,
      force_p_newlines: false,
      forced_root_block: false,

      setup(editor) {
        self.mceEditorInstance = editor

        editor.on('keydown', (e) => {
          self.onKeydown(e)
        })

        editor.on('paste', (e) => {
          self.onPaste(e)
        })

        editor.on('init', () => {
          self.anchorClickableLink(self.value)
        })
      },
      init_instance_callback(editor) {
        self.cantUseTinyMCE = false

        editor.on('ExecCommand', (e) => {
          self.handleTrackEditing(e.command, e.value)
          const commands = ['JustifyCenter', 'JustifyLeft', 'JustifyRight']
          if (commands.includes(e.command)) {
            self.handleClickTextAlignment(e.command)
          }
        })
      },
    }
  },
  mounted() {
    EventBus.$on('right-column: text-alignment', (alignmentValue) => {
      this.mceEditorInstance.execCommand(alignmentValue)
    })
  },
  destroyed() {
    EventBus.$off('right-column: text-alignment')
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction', 'blockHistoryAction', 'unblockHistoryAction', 'addActionToHistory']),

    onChangeEditorValue: debounce(function debounceChangeEditorValue() {
      this._handleEmitUpdateValue()
    }, 500, { leading: true }),

    _handleEmitUpdateValue() {
      const editor = this.mceEditorInstance
      if (!editor) return
      let updatedText = editor.targetElm.innerText
      if (editor.targetElm.innerHTML.indexOf('<br>') !== -1) {
        editor.targetElm.innerHTML = editor.targetElm.innerHTML.replaceAll('<br>', ' ')
      }
      if (editor.targetElm.innerText.length > this.maxChars) {
        updatedText = updatedText.substring(0, this.maxChars)
      }
      this.$emit('update', this.type, {
        id: this.mceEditorInstance.slideId,
        title: updatedText,
        titleHTML: editor.targetElm.innerHTML,
      })
    },

    handleClickTextAlignment(alignmentValue) {
      const textAlign = this._getTextAlignmentValue(alignmentValue)

      this.$emit('update', this.type, {
        id: this.mceEditorInstance.slideId,
        textAlign,
        title: this.mceEditorInstance.getContent({ format: 'text' }),
        titleHTML: this.mceEditorInstance.getContent({ format: 'raw' }),
      })
    },

    _getTextAlignmentValue(value) {
      if (value === 'JustifyLeft') {
        return 'left'
      }
      if (value === 'JustifyRight') {
        return 'right'
      }
      return 'center'
    },

    onBlur() {
      window.getSelection().empty()
      this.onChangeEditorValue()
      this._setInputBlur()
      this.unblockHistoryAction()
      // this.forceBlurEditor()
    },

    _setInputBlur() {
      setTimeout(() => {
        if (this.$refs[this.inputId]) {
          this.$refs[this.inputId].$el.blur()
        }
      }, 0)
    },

    forceBlurEditor() {
      this.mceEditorInstance.setMode('readonly')
    },

    onFocus(e) {
      this.blockHistoryAction()
      this.mceEditorInstance.slideId = this.id

      this.isInputOnFocus = true
      if (e.target &&
        e.target.targetElm &&
        e.target.targetElm.innerText === util.extractContent(this.initialValue, true)) {
        window.getSelection().selectAllChildren(
          e.target.targetElm,
        )
      }
    },

    handleTrackEditing(command, value) {
      const trackableCommands = [
        'mceApplyTextColor',
        'FontSize',
        'FontName',
        'mceToggleFormat',
        'mceApplyTextcolor',
        'JustifyCenter',
        'JustifyFull',
        'JustifyLeft',
        'JustifyRight',
        'InsertOrderedList',
        'InsertUnorderedList',
        'mceInsertLink',
        'mceShowCharmap',
      ]
      if (!trackableCommands.includes(command)) return
      util.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        `Edit slide body > ${command}`,
        value,
        this.presentation.id,
        {
          slideId: this.slideActive.id,
          presentationId: this.presentation.id,
        },
      )
    },

    onKeydown(e) {
      if (e.keyCode === 13) {
        e.preventDefault() // prevent enter a new line of the heading
        this.onBlur()
        return false
      }
      const wordcountPlugin = window.tinymce.activeEditor.plugins.wordcount.body
      const currentLen = wordcountPlugin.getCharacterCount()
      const nonCharKeys = [8, 16, 17, 18, 20, 33, 34, 35, 36, 37, 38, 39, 40, 46, 91, 93, 224]

      if (!e.ctrlKey && !e.metaKey && !nonCharKeys.includes(e.keyCode)
        && currentLen >= this.maxChars) {
        e.preventDefault()
        e.stopPropagation()
        return false
      }
      return true
    },

    onPaste(e) {
      setTimeout(() => {
        if (!e.currentTarget) return
        const currentLength = e.currentTarget.innerText.length
        if (currentLength > this.maxChars) {
          e.currentTarget.innerText = e.currentTarget.innerText.substring(0, this.maxChars)
        }
      }, 0)
    },
    onClick() {
      if (!this.disabled && this.mceEditorInstance) {
        this.mceEditorInstance.setMode('design')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.editor__wrapper {
  overflow-y: hidden;
  width: 100%;
  cursor: text;
  border: 0.5px solid transparent;
  line-height: 1.2;
}
.mce-editor {
  -webkit-user-select: text; // Fix bug: editor doesn't work on Safari
  cursor: text;
  border: 0.5px solid transparent;
  padding: 5px 10px;
  &:hover, &:focus, &:active {
    border: 0.5px solid rgba(49, 49, 49, 0.7);
  }
  &.light-text-colour {
    &:hover, &:focus, &:active {
      border: 0.5px solid rgba(255, 255, 255, 0.7);
    }
  }
  &.has-border {
    border: 0.5px solid rgba(49, 49, 49, 0.4);
  }
  &.has-border.light-text-colour {
    border: 0.5px solid rgba(255, 255, 255, 0.4);
  }
}

.slide-presenting-mode{
  .mce-editor{
    &.clickable{
      pointer-events: inherit;
    }
  }
}
</style>
